import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/client';
import { CurrencyDollar, Plus, Receipt, Download, WhatsappLogo, Pencil, Trash, CaretDown, CaretUp, CheckCircle, Clock, WarningCircle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

function Payments() {
  const location = useLocation();
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [activeTab, setActiveTab] = useState('payments');
  const [reminderMonth, setReminderMonth] = useState(() => {
    const now = new Date();
    return months_list()[now.getMonth()];
  });
  const [reminderYear, setReminderYear] = useState(new Date().getFullYear());
  const [reminderBatch, setReminderBatch] = useState('all');
  const [absentStudentIds, setAbsentStudentIds] = useState([]);
  const [paymentBatchFilter, setPaymentBatchFilter] = useState('all');
  const [paymentSearch, setPaymentSearch] = useState('');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [tutorBatchIds, setTutorBatchIds] = useState([]);
  const [formData, setFormData] = useState({
    student_id: '',
    amount: '',
    month: '',
    year: new Date().getFullYear(),
    payment_method: 'cash',
    notes: '',
  });

  function months_list() {
    return [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
  }

  const months = months_list();

  useEffect(() => {
    fetchPayments();
    fetchStudents();
    fetchBatches();
  }, []);

  useEffect(() => {
    const fetchAbsences = async () => {
      try {
        const response = await api.get(`/absences?month=${reminderMonth}&year=${reminderYear}`);
        setAbsentStudentIds(response.data.map(a => a.student_id));
      } catch (error) {
        setAbsentStudentIds([]);
      }
    };
    if (reminderMonth && reminderYear) fetchAbsences();
  }, [reminderMonth, reminderYear]);

  // Handle prefill from Students page navigation
  useEffect(() => {
    if (location.state?.prefillStudent && students.length > 0) {
      const student = students.find(s => s.id === location.state.prefillStudent);
      if (student) {
        const batch = batches.find(b => b.id === student.batch_id);
        setPaymentBatchFilter(student.batch_id);
        setFormData(prev => ({
          ...prev,
          student_id: student.id,
          month: months_list()[new Date().getMonth()],
          year: new Date().getFullYear(),
        }));
        setIsDialogOpen(true);
        // Clear the state so it doesn't re-trigger
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, students, batches]);

  const fetchPayments = async () => {
    try {
      const response = await api.get('/payments');
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await api.get('/students');
      setStudents(response.data.filter(s => s.is_active));
    } catch (error) {
      toast.error('Failed to fetch students');
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
      const tutorIds = response.data.filter(b => b.name.toLowerCase().startsWith('tutor')).map(b => b.id);
      setTutorBatchIds(tutorIds);
    } catch (error) {
      toast.error('Failed to fetch batches');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const student = students.find(s => s.id === formData.student_id);
    if (!student) {
      toast.error('Student not found');
      return;
    }
    try {
      await api.post('/payments', {
        student_id: formData.student_id,
        student_name: student.name,
        batch_id: student.batch_id,
        amount: parseFloat(formData.amount),
        month: formData.month,
        year: parseInt(formData.year),
        payment_method: formData.payment_method,
        notes: formData.notes,
      });
      toast.success('Payment recorded successfully');
      setIsDialogOpen(false);
      setFormData({ student_id: '', amount: '', month: '', year: new Date().getFullYear(), payment_method: 'cash', notes: '' });
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const getBatchName = (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    return batch ? batch.name : 'Unknown';
  };

  const handleDownloadInvoice = async (paymentId) => {
    try {
      const response = await api.get(`/invoices/generate/${paymentId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleEdit = (payment) => {
    setSelectedPayment(payment);
    setFormData({
      student_id: payment.student_id,
      amount: payment.amount.toString(),
      month: payment.month,
      year: payment.year,
      payment_method: payment.payment_method,
      notes: payment.notes || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/payments/${selectedPayment.id}`, {
        amount: parseFloat(formData.amount),
        month: formData.month,
        year: parseInt(formData.year),
        payment_method: formData.payment_method,
        notes: formData.notes,
      });
      toast.success('Payment updated successfully');
      setIsEditDialogOpen(false);
      setSelectedPayment(null);
      fetchPayments();
    } catch (error) {
      toast.error('Failed to update payment');
    }
  };

  const handleDelete = async (paymentId) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await api.delete(`/payments/${paymentId}`);
        toast.success('Payment deleted successfully');
        fetchPayments();
      } catch (error) {
        toast.error('Failed to delete payment');
      }
    }
  };

  const getSortedPayments = () => {
    let filtered = [...payments];
    
    // Filter by search
    if (paymentSearch.trim()) {
      const q = paymentSearch.toLowerCase();
      filtered = filtered.filter(p => (p.student_name || '').toLowerCase().includes(q));
    }
    // Filter by batch
    if (paymentBatchFilter !== 'all') {
      filtered = filtered.filter(p => p.batch_id === paymentBatchFilter);
    }
    // Filter by month
    if (filterMonth !== 'all') {
      filtered = filtered.filter(p => p.month === filterMonth);
    }
    // Filter by year
    if (filterYear !== 'all') {
      filtered = filtered.filter(p => p.year === parseInt(filterYear));
    }
    // Filter by payment method
    if (filterMethod !== 'all') {
      filtered = filtered.filter(p => p.payment_method === filterMethod);
    }
    // Filter by date range
    if (filterDateFrom) {
      filtered = filtered.filter(p => p.payment_date && new Date(p.payment_date) >= new Date(filterDateFrom));
    }
    if (filterDateTo) {
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59);
      filtered = filtered.filter(p => p.payment_date && new Date(p.payment_date) <= toDate);
    }
    
    // Sort
    switch (sortBy) {
      case 'student':
        return filtered.sort((a, b) => a.student_name.localeCompare(b.student_name));
      case 'batch':
        return filtered.sort((a, b) => getBatchName(a.batch_id).localeCompare(getBatchName(b.batch_id)));
      case 'month':
        return filtered.sort((a, b) => {
          return months.indexOf(a.month) - months.indexOf(b.month) || b.year - a.year;
        });
      default:
        return filtered.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));
    }
  };

  const clearFilters = () => {
    setPaymentSearch('');
    setPaymentBatchFilter('all');
    setFilterMonth('all');
    setFilterYear('all');
    setFilterMethod('all');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const hasActiveFilters = paymentSearch || paymentBatchFilter !== 'all' || filterMonth !== 'all' || filterYear !== 'all' || filterMethod !== 'all' || filterDateFrom || filterDateTo;

  const getStudentPaymentStatus = () => {
    let filteredStudents = [...students]
      .filter(s => !tutorBatchIds.includes(s.batch_id))
      .filter(s => !absentStudentIds.includes(s.id))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (reminderBatch !== 'all') {
      filteredStudents = filteredStudents.filter(s => s.batch_id === reminderBatch);
    }

    return filteredStudents.map(student => {
      const paid = payments.find(
        p => p.student_id === student.id && p.month === reminderMonth && p.year === reminderYear
      );
      return {
        ...student,
        isPaid: !!paid,
        paymentDetails: paid || null,
      };
    });
  };

  const sendWhatsAppReminder = (student) => {
    const phone = student.parent_phone || student.phone || '';
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const message = `Dear Parent of ${student.name},\n\nA Quick Reminder to Settle the Fees for the month of ${reminderMonth} ${reminderYear}.\n\nPlease make the payment at your earliest convenience.\n\nDillusions Dance Academy`;
    const encodedMessage = encodeURIComponent(message);
    if (!cleanPhone) {
      toast.error('No phone number found for this student');
      return;
    }
    window.open(`https://wa.me/${cleanPhone}?text=${encodedMessage}`, '_blank');
  };

  const sendPaymentReceipt = (payment) => {
    const student = students.find(s => s.id === payment.student_id);
    const phone = student ? (student.parent_phone || student.phone || '') : '';
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const payDate = new Date(payment.payment_date);
    const formattedDate = `${payDate.getDate().toString().padStart(2,'0')}/${(payDate.getMonth()+1).toString().padStart(2,'0')}/${payDate.getFullYear()}`;
    const message = `Dear Parent of ${payment.student_name},\n\nReceived the fee of KWD ${payment.amount} for ${payment.month} ${payment.year}\n\nPayment Method: ${payment.payment_method}\nDate: ${formattedDate}\n\nDillusions Dance Academy`;
    const encodedMessage = encodeURIComponent(message);
    if (!cleanPhone) {
      toast.error('No phone number found for this student');
      return;
    }
    window.open(`https://wa.me/${cleanPhone}?text=${encodedMessage}`, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  const studentStatuses = getStudentPaymentStatus();
  const pendingCount = studentStatuses.filter(s => !s.isPaid).length;
  const paidCount = studentStatuses.filter(s => s.isPaid).length;

  return (
    <Layout>
      <div className="p-6 sm:p-8 lg:p-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-50 mb-2">
              Fee Payments
            </h1>
            <p className="text-base text-slate-400">Track monthly fee payments for students</p>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="record-payment-button"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-6 py-3 rounded-md transition-colors"
              >
                <Plus size={20} weight="bold" />
                Record Payment
              </button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-slate-50 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-slate-50">Record Fee Payment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Filter by Batch</label>
                  <select
                    value={paymentBatchFilter}
                    onChange={(e) => { setPaymentBatchFilter(e.target.value); setFormData({ ...formData, student_id: '' }); }}
                    data-testid="payment-batch-filter"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-2.5 text-sm text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 mb-3"
                  >
                    <option value="all">All Batches</option>
                    {batches.filter(b => !tutorBatchIds.includes(b.id)).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>

                  <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Search Student</label>
                  <input
                    type="text"
                    value={paymentSearch}
                    onChange={(e) => setPaymentSearch(e.target.value)}
                    placeholder="Type student name..."
                    data-testid="payment-student-search"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-2.5 text-sm text-slate-50 placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 mb-3"
                  />

                  <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Select Student</label>
                  <Select value={formData.student_id} onValueChange={(value) => setFormData({ ...formData, student_id: value })} required>
                    <SelectTrigger data-testid="payment-student-select" className="bg-slate-950 border-slate-800 text-slate-50">
                      <SelectValue placeholder="Choose student" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                      {[...students]
                        .filter(s => !tutorBatchIds.includes(s.batch_id))
                        .filter(s => paymentBatchFilter === 'all' || s.batch_id === paymentBatchFilter)
                        .filter(s => !paymentSearch.trim() || s.name.toLowerCase().includes(paymentSearch.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((student) => (
                        <SelectItem key={student.id} value={student.id} className="text-slate-50">
                          {student.name} - {getBatchName(student.batch_id)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Month</label>
                    <Select value={formData.month} onValueChange={(value) => setFormData({ ...formData, month: value })} required>
                      <SelectTrigger data-testid="payment-month-select" className="bg-slate-950 border-slate-800 text-slate-50">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        {months.map((month) => (
                          <SelectItem key={month} value={month} className="text-slate-50">{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Year</label>
                    <Input data-testid="payment-year-input" type="number" value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Amount (KWD)</label>
                    <Input data-testid="payment-amount-input" type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" placeholder="0.00" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Payment Method</label>
                    <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                      <SelectTrigger data-testid="payment-method-select" className="bg-slate-950 border-slate-800 text-slate-50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="cash" className="text-slate-50">Cash</SelectItem>
                        <SelectItem value="card" className="text-slate-50">Card</SelectItem>
                        <SelectItem value="online" className="text-slate-50">Online</SelectItem>
                        <SelectItem value="cheque" className="text-slate-50">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Notes (Optional)</label>
                  <Input data-testid="payment-notes-input" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" placeholder="Additional notes" />
                </div>
                <Button type="submit" data-testid="submit-payment-button" className="w-full bg-blue-600 hover:bg-blue-500">Record Payment</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tab Toggle */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => setActiveTab('payments')}
            data-testid="tab-payments"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm transition-colors ${
              activeTab === 'payments'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border border-slate-800'
            }`}
          >
            <Receipt size={18} weight="bold" />
            Payment Records
          </button>
          <button
            onClick={() => setActiveTab('reminders')}
            data-testid="tab-reminders"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm transition-colors ${
              activeTab === 'reminders'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border border-slate-800'
            }`}
          >
            <WhatsappLogo size={18} weight="bold" />
            Fee Reminders
          </button>
        </div>

        {/* Payment Records Tab */}
        {activeTab === 'payments' && (
          <>
            {/* Filters */}
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 items-end">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Search</label>
                  <Input
                    data-testid="payment-search"
                    value={paymentSearch}
                    onChange={(e) => setPaymentSearch(e.target.value)}
                    placeholder="Student name..."
                    className="bg-slate-950 border-slate-800 text-slate-50 text-sm h-9"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Batch</label>
                  <Select value={paymentBatchFilter} onValueChange={setPaymentBatchFilter}>
                    <SelectTrigger data-testid="filter-batch" className="bg-slate-950 border-slate-800 text-slate-50 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="all" className="text-slate-50">All Batches</SelectItem>
                      {batches.map(b => <SelectItem key={b.id} value={b.id} className="text-slate-50">{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Month</label>
                  <Select value={filterMonth} onValueChange={setFilterMonth}>
                    <SelectTrigger data-testid="filter-month" className="bg-slate-950 border-slate-800 text-slate-50 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="all" className="text-slate-50">All Months</SelectItem>
                      {months.map(m => <SelectItem key={m} value={m} className="text-slate-50">{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Year</label>
                  <Select value={filterYear} onValueChange={setFilterYear}>
                    <SelectTrigger data-testid="filter-year" className="bg-slate-950 border-slate-800 text-slate-50 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="all" className="text-slate-50">All Years</SelectItem>
                      {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)} className="text-slate-50">{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Method</label>
                  <Select value={filterMethod} onValueChange={setFilterMethod}>
                    <SelectTrigger data-testid="filter-method" className="bg-slate-950 border-slate-800 text-slate-50 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="all" className="text-slate-50">All Methods</SelectItem>
                      <SelectItem value="cash" className="text-slate-50">Cash</SelectItem>
                      <SelectItem value="card" className="text-slate-50">Card</SelectItem>
                      <SelectItem value="online" className="text-slate-50">Online</SelectItem>
                      <SelectItem value="cheque" className="text-slate-50">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Date From</label>
                  <Input
                    data-testid="filter-date-from"
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-50 text-sm h-9"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Date To</label>
                  <Input
                    data-testid="filter-date-to"
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-50 text-sm h-9"
                  />
                </div>
              </div>
              {hasActiveFilters && (
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-800">
                  <span className="text-xs text-slate-400">{getSortedPayments().length} of {payments.length} payments</span>
                  <button onClick={clearFilters} data-testid="clear-filters" className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium">
                    Clear all filters
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-48">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger data-testid="sort-select" className="bg-slate-900 border-slate-800 text-slate-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="date" className="text-slate-50">Sort by Date</SelectItem>
                    <SelectItem value="student" className="text-slate-50">Sort by Student</SelectItem>
                    <SelectItem value="batch" className="text-slate-50">Sort by Batch</SelectItem>
                    <SelectItem value="month" className="text-slate-50">Sort by Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {payments.length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
                <Receipt size={64} weight="duotone" className="mx-auto text-slate-700 mb-4" />
                <h3 className="text-xl font-semibold text-slate-300 mb-2">No payments yet</h3>
                <p className="text-slate-500 mb-6">Record your first payment to get started</p>
              </div>
            ) : (
              <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-950 border-b border-slate-800">
                      <tr>
                        <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Student</th>
                        <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Batch</th>
                        <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Month</th>
                        <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Amount</th>
                        <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Method</th>
                        <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Date</th>
                        <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {getSortedPayments().map((payment) => (
                        <tr key={payment.id} data-testid={`payment-row-${payment.id}`} className="hover:bg-slate-800/50">
                          <td className="px-6 py-4 text-slate-50 font-medium">{payment.student_name}</td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded-full text-sm font-medium">
                              {getBatchName(payment.batch_id)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-slate-400">{payment.month} {payment.year}</td>
                          <td className="px-6 py-4">
                            <span className="text-emerald-400 font-semibold">KWD {payment.amount}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-slate-800 text-slate-300 rounded-md text-sm capitalize">{payment.payment_method}</span>
                          </td>
                          <td className="px-4 py-4 text-slate-400 whitespace-nowrap text-sm">
                            {new Date(payment.payment_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button onClick={() => handleDownloadInvoice(payment.id)} data-testid={`download-invoice-${payment.id}`} className="p-2 hover:bg-slate-700 rounded-md transition-colors text-cyan-400 hover:text-cyan-300" title="Download Invoice">
                                <Download size={18} weight="duotone" />
                              </button>
                              <button onClick={() => sendPaymentReceipt(payment)} data-testid={`whatsapp-receipt-${payment.id}`} className="p-2 hover:bg-slate-700 rounded-md transition-colors text-emerald-400 hover:text-emerald-300" title="Send Receipt via WhatsApp">
                                <WhatsappLogo size={18} weight="duotone" />
                              </button>
                              <button onClick={() => handleEdit(payment)} data-testid={`edit-payment-${payment.id}`} className="p-2 hover:bg-slate-700 rounded-md transition-colors text-blue-400 hover:text-blue-300" title="Edit Payment">
                                <Pencil size={18} weight="duotone" />
                              </button>
                              <button onClick={() => handleDelete(payment.id)} data-testid={`delete-payment-${payment.id}`} className="p-2 hover:bg-slate-700 rounded-md transition-colors text-rose-400 hover:text-rose-300" title="Delete Payment">
                                <Trash size={18} weight="duotone" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Total Fees */}
                <div className="border-t border-slate-800 px-6 py-4 flex justify-end">
                  <div className="flex items-center gap-3" data-testid="total-fees">
                    <span className="text-sm font-bold uppercase tracking-wider text-slate-400">Total:</span>
                    <span className="text-xl font-bold text-emerald-400">
                      KWD {getSortedPayments().reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Fee Reminders Tab */}
        {activeTab === 'reminders' && (
          <>
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold text-slate-50 mb-4">Select Month & Batch</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Month</label>
                  <select
                    value={reminderMonth}
                    onChange={(e) => setReminderMonth(e.target.value)}
                    data-testid="reminder-month-select"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    {months.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Year</label>
                  <input
                    type="number"
                    value={reminderYear}
                    onChange={(e) => setReminderYear(parseInt(e.target.value))}
                    data-testid="reminder-year-input"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Batch</label>
                  <select
                    value={reminderBatch}
                    onChange={(e) => setReminderBatch(e.target.value)}
                    data-testid="reminder-batch-select"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="all">All Batches</option>
                    {batches.filter(b => !tutorBatchIds.includes(b.id)).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                  <CurrencyDollar size={22} weight="duotone" className="text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Total Students</p>
                  <p className="text-xl font-bold text-slate-50">{studentStatuses.length}</p>
                </div>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                  <CheckCircle size={22} weight="duotone" className="text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Paid</p>
                  <p className="text-xl font-bold text-emerald-400">{paidCount}</p>
                </div>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                  <WarningCircle size={22} weight="duotone" className="text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Pending</p>
                  <p className="text-xl font-bold text-amber-400">{pendingCount}</p>
                </div>
              </div>
            </div>

            {/* Student Payment Status List */}
            <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-950 border-b border-slate-800">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">S.No</th>
                      <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Student</th>
                      <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Batch</th>
                      <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Status</th>
                      <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {studentStatuses.map((student, idx) => (
                      <tr key={student.id} data-testid={`reminder-row-${student.id}`} className="hover:bg-slate-800/50">
                        <td className="px-6 py-4 text-slate-400 font-medium">{idx + 1}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {student.photo_url ? (
                              <img src={student.photo_url} alt={student.name} className="w-8 h-8 rounded-full object-cover border border-slate-700" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center">
                                <span className="text-xs font-bold text-blue-400">{student.name.charAt(0)}</span>
                              </div>
                            )}
                            <span className="text-slate-50 font-medium">{student.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded-full text-sm font-medium">
                            {getBatchName(student.batch_id)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {student.isPaid ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-600/20 text-emerald-400 rounded-full text-sm font-medium">
                              <CheckCircle size={14} weight="bold" />
                              Paid
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-600/20 text-amber-400 rounded-full text-sm font-medium">
                              <Clock size={14} weight="bold" />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {!student.isPaid && (
                            <button
                              onClick={() => sendWhatsAppReminder(student)}
                              data-testid={`send-reminder-${student.id}`}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-md text-sm font-medium transition-colors"
                            >
                              <WhatsappLogo size={16} weight="bold" />
                              Send Reminder
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-4 bg-slate-950 border-t border-slate-800">
                <p className="text-sm text-slate-400">
                  {reminderMonth} {reminderYear} — <span className="text-emerald-400 font-medium">{paidCount} paid</span>, <span className="text-amber-400 font-medium">{pendingCount} pending</span>
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-50">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-50">Edit Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Month</label>
                <Select value={formData.month} onValueChange={(value) => setFormData({ ...formData, month: value })}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-50"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {months.map((month) => (
                      <SelectItem key={month} value={month} className="text-slate-50">{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Year</label>
                <Input type="number" value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Amount (KWD)</label>
                <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" required />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Payment Method</label>
                <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-50"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="cash" className="text-slate-50">Cash</SelectItem>
                    <SelectItem value="card" className="text-slate-50">Card</SelectItem>
                    <SelectItem value="online" className="text-slate-50">Online</SelectItem>
                    <SelectItem value="cheque" className="text-slate-50">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Notes</label>
              <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" />
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-500">Update Payment</Button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default Payments;
